import React from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import type { AxiosResponse } from 'axios';

import type { IAgent } from '@/store/types';
import type { IReinstateAgentRequest } from '@/api/types';

import { ModalFooter } from '@/components/common';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { reinstateAgent } from '@/store/slices/contractSlice';
import { isRejectedAction } from '@/helpers';
import { createSelector } from '@reduxjs/toolkit';
import { selectIsPendingAction } from '@/store/selectors/contractSelectors';

interface IProps {
  agent: IAgent;
  handleClose: () => void;
}

const reinstateAgentModalSelector = createSelector(selectIsPendingAction(reinstateAgent.typePrefix), isPending => ({
  isPending,
}));

const ReinstateAgentModal = ({ handleClose, agent }: IProps): React.ReactElement => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { isPending } = useAppSelector(reinstateAgentModalSelector);

  const reinstate = async () => {
    const request: IReinstateAgentRequest = {
      resTermId: agent.resTermId ?? 0,
      userId: agent.userId,
      agentName: `${agent.firstName} ${agent.lastName}`,
      agentNameFirst: agent.firstName,
      agentNameLast: agent.lastName,
      contractId: agent.contractId,
      contractName: agent.contractName,
      contractStartBlock: agent.contractStartBlock,
      contractAssnStartBlock: agent.contractAssnStartBlock,
      positionId: agent.jobId,
      positionName: agent.jobName || '',
      clientId: agent.clientId,
      clientName: agent.clientName || '',
      reasonId: agent.resTermReasonId ?? 0,
      reasonText: agent.resTermReasonText ?? '',
      comment: agent.commentToLX ?? '',
    };
    const returnAction = await dispatch(reinstateAgent(request));
    if (isRejectedAction(returnAction)) {
      throw (returnAction as { payload: AxiosResponse<string> }).payload.data;
    }
  };

  return (
    <>
      <Modal.Body>{t('clientGigs.modal.reinstate.text', { name: `${agent.firstName} ${agent.lastName}` })}</Modal.Body>
      <ModalFooter
        onSecondaryBtnClick={handleClose}
        primaryBtnText={t`clientGigs.modal.reinstate.btnSubmit`}
        secondaryBtnText={t`clientGigs.modal.reinstate.btnCancel`}
        buttonsSize="grow"
        primaryDisabled={isPending}
        secondaryDisabled={isPending}
        onPrimaryBtnClick={async () => {
          try {
            await reinstate();
            toast.success(t('clientGigs.modal.reinstate.success', { name: `${agent.firstName} ${agent.lastName}` }));
          } catch (e) {
            toast.error(e);
          }
          handleClose();
        }}
      />
    </>
  );
};

export { ReinstateAgentModal };
